import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getPendingApprovalsThunk,
  approveRequestThunk,
  rejectRequestThunk,
  getApprovalHistoryThunk,
} from "../../../store/thunks/permissionThunks";
import Button from "../../../components/common/Button";
import Badge from "../../../components/common/Badge";
import Modal from "../../../components/common/Modal";
import {
  APPROVAL_STATUS_COLORS,
  APPROVAL_TYPES,
} from "../../../constants/admin.constants";
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  ExclamationIcon,
} from "@heroicons/react/outline";

const ApprovalRequestsPanel = () => {
  const dispatch = useDispatch();
  const { pendingApprovals, approvalHistory, loadingApprovals } = useSelector(
    (state) => state.adminPermission
  );

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [actionModal, setActionModal] = useState({
    open: false,
    type: null,
    request: null,
  });
  const [comments, setComments] = useState("");
  const [filter, setFilter] = useState("PENDING");

  // Add ref to track if history has been loaded
  const historyLoadedRef = useRef(false);
  const pendingLoadedRef = useRef(false);
  const lastFilterRef = useRef(filter);

  // Load pending approvals only once on mount
  useEffect(() => {
    if (!pendingLoadedRef.current) {
      pendingLoadedRef.current = true;
      dispatch(getPendingApprovalsThunk());
    }
  }, []); // Empty dependency array

  // Load history only when switching to history view
  useEffect(() => {
    if (showHistory && !historyLoadedRef.current) {
      historyLoadedRef.current = true;
      lastFilterRef.current = filter;
      dispatch(getApprovalHistoryThunk({ status: filter }));
    }
  }, [showHistory]); // Don't include filter here

  // Handle filter changes separately
  useEffect(() => {
    if (
      showHistory &&
      historyLoadedRef.current &&
      filter !== lastFilterRef.current
    ) {
      lastFilterRef.current = filter;
      dispatch(getApprovalHistoryThunk({ status: filter }));
    }
  }, [filter, showHistory]); // Only re-run when filter changes

  const handleApprove = async (approvalId) => {
    if (window.confirm("Are you sure you want to approve this request?")) {
      try {
        await dispatch(approveRequestThunk({ approvalId, comments })).unwrap();
        setActionModal({ open: false, type: null, request: null });
        setComments("");
        // Refresh pending approvals after action
        dispatch(getPendingApprovalsThunk());
      } catch (error) {
        console.error("Failed to approve:", error);
      }
    }
  };

  const handleReject = async (approvalId) => {
    if (!comments.trim()) {
      alert("Please provide a reason for rejection");
      return;
    }
    if (window.confirm("Are you sure you want to reject this request?")) {
      try {
        await dispatch(
          rejectRequestThunk({ approvalId, reason: comments })
        ).unwrap();
        setActionModal({ open: false, type: null, request: null });
        setComments("");
        // Refresh pending approvals after action
        dispatch(getPendingApprovalsThunk());
      } catch (error) {
        console.error("Failed to reject:", error);
      }
    }
  };

  const getRequestTypeIcon = (type) => {
    switch (type) {
      case "RATE_UPDATE":
        return "💹";
      case "PRICING_RULE_CREATE":
        return "💰";
      case "OFFER_CREATE":
        return "🎁";
      case "HIGH_DISCOUNT":
        return "🏷️";
      default:
        return "📋";
    }
  };

  const formatRequestDetails = (request) => {
    const details = request.requestDetails;

    switch (request.type) {
      case "RATE_UPDATE":
        return (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Type:</span>
              <span className="font-medium">{details.rateChange?.type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Old Rate:</span>
              <span className="font-medium">
                ₹{details.rateChange?.oldRate}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">New Rate:</span>
              <span className="font-medium text-green-600">
                ₹{details.rateChange?.newRate}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Change:</span>
              <span
                className={`font-medium ${
                  details.rateChange?.changePercent > 0
                    ? "text-red-600"
                    : "text-green-600"
                }`}
              >
                {details.rateChange?.changePercent?.toFixed(2)}%
              </span>
            </div>
          </div>
        );

      case "PRICING_RULE_CREATE":
        return (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Name:</span>
              <span className="font-medium">{details.pricingRule?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Priority:</span>
              <span className="font-medium">
                {details.pricingRule?.priority}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Making Charge:</span>
              <span className="font-medium">
                {details.pricingRule?.makingChargePercent}%
              </span>
            </div>
          </div>
        );

      case "OFFER_CREATE":
        return (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Offer Code:</span>
              <span className="font-medium">{details.offer?.offerCode}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Discount:</span>
              <span className="font-medium text-green-600">
                {details.offer?.discountPercent
                  ? `${details.offer.discountPercent}%`
                  : `₹${details.offer?.discountAmount}`}
              </span>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-sm text-gray-600">
            {details.reason || "No additional details available"}
          </div>
        );
    }
  };

  const approvalsList = showHistory ? approvalHistory : pendingApprovals;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold">
            {showHistory ? "Approval History" : "Pending Approvals"}
          </h3>
          {pendingApprovals.length > 0 && !showHistory && (
            <Badge variant="warning">{pendingApprovals.length} Pending</Badge>
          )}
        </div>

        <div className="flex gap-2">
          {showHistory && (
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-1 border rounded-lg text-sm"
            >
              <option value="ALL">All</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="EXPIRED">Expired</option>
            </select>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
          >
            {showHistory ? "Show Pending" : "View History"}
          </Button>
        </div>
      </div>

      {/* Approvals List */}
      {loadingApprovals ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : approvalsList.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">
            {showHistory ? "No approval history found" : "No pending approvals"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {approvalsList.map((request) => (
            <div
              key={request._id}
              className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  {/* Request Header */}
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">
                      {getRequestTypeIcon(request.type)}
                    </span>
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {APPROVAL_TYPES.find((t) => t === request.type) ||
                          request.type}
                      </h4>
                      <p className="text-sm text-gray-600">
                        Requested by {request.requestedBy?.adminName}
                      </p>
                    </div>
                  </div>

                  {/* Request Details */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-3">
                    {formatRequestDetails(request)}
                  </div>

                  {/* Metadata */}
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <ClockIcon className="w-4 h-4" />
                      {new Date(request.createdAt).toLocaleString()}
                    </span>
                    {request.expiresAt && request.status === "PENDING" && (
                      <span className="flex items-center gap-1 text-orange-600">
                        <ExclamationIcon className="w-4 h-4" />
                        Expires: {new Date(request.expiresAt).toLocaleString()}
                      </span>
                    )}
                  </div>

                  {/* Approval/Rejection Info */}
                  {request.status === "APPROVED" && request.approvedBy && (
                    <div className="mt-3 p-2 bg-green-50 rounded-lg">
                      <p className="text-sm text-green-800">
                        ✅ Approved by {request.approvedBy.adminName} on{" "}
                        {new Date(
                          request.approvedBy.approvedAt
                        ).toLocaleString()}
                      </p>
                      {request.approvedBy.comments && (
                        <p className="text-sm text-gray-600 mt-1">
                          Comments: {request.approvedBy.comments}
                        </p>
                      )}
                    </div>
                  )}

                  {request.status === "REJECTED" && request.rejectedBy && (
                    <div className="mt-3 p-2 bg-red-50 rounded-lg">
                      <p className="text-sm text-red-800">
                        ❌ Rejected by {request.rejectedBy.adminName} on{" "}
                        {new Date(
                          request.rejectedBy.rejectedAt
                        ).toLocaleString()}
                      </p>
                      {request.rejectedBy.reason && (
                        <p className="text-sm text-gray-600 mt-1">
                          Reason: {request.rejectedBy.reason}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="ml-4">
                  <Badge variant={APPROVAL_STATUS_COLORS[request.status]}>
                    {request.status}
                  </Badge>
                  {request.status === "PENDING" && (
                    <div className="flex gap-2 mt-3">
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() =>
                          setActionModal({
                            open: true,
                            type: "approve",
                            request,
                          })
                        }
                      >
                        <CheckCircleIcon className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() =>
                          setActionModal({
                            open: true,
                            type: "reject",
                            request,
                          })
                        }
                      >
                        <XCircleIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Modal */}
      <Modal
        isOpen={actionModal.open}
        closeModal={() => {
          setActionModal({ open: false, type: null, request: null });
          setComments("");
        }}
        title={
          actionModal.type === "approve" ? "Approve Request" : "Reject Request"
        }
      >
        {actionModal.request && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-medium mb-2">{actionModal.request.type}</p>
              {formatRequestDetails(actionModal.request)}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {actionModal.type === "approve"
                  ? "Comments (Optional)"
                  : "Reason for Rejection"}
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                rows="3"
                placeholder={
                  actionModal.type === "approve"
                    ? "Add any comments about this approval..."
                    : "Please provide a reason for rejection..."
                }
                required={actionModal.type === "reject"}
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setActionModal({ open: false, type: null, request: null });
                  setComments("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant={actionModal.type === "approve" ? "success" : "danger"}
                onClick={() => {
                  if (actionModal.type === "approve") {
                    handleApprove(actionModal.request._id);
                  } else {
                    handleReject(actionModal.request._id);
                  }
                }}
              >
                {actionModal.type === "approve" ? "Approve" : "Reject"}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ApprovalRequestsPanel;
